import jsPDF from "jspdf";
import type { OptimizeResult, UsedSheet } from "./types.ts";
import { t, type Lang } from "./i18n.ts";

const COLORS = [
  "#3b82f6", "#ef4444", "#22c55e", "#f59e0b", "#8b5cf6",
  "#ec4899", "#06b6d4", "#f97316", "#14b8a6", "#6366f1",
  "#d946ef", "#84cc16", "#e11d48", "#0ea5e9", "#a855f7",
  "#10b981", "#f43f5e", "#7c3aed", "#eab308", "#2563eb",
];

function hexToRGB(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

let fontBase64: string | null = null;

async function loadFont(doc: jsPDF) {
  if (!fontBase64) {
    const res = await fetch("/fonts/Inter.ttf");
    const buf = await res.arrayBuffer();
    fontBase64 = btoa(
      new Uint8Array(buf).reduce((s, b) => s + String.fromCharCode(b), ""),
    );
  }
  doc.addFileToVFS("Inter.ttf", fontBase64);
  doc.addFont("Inter.ttf", "Inter", "normal");
}

function setFont(doc: jsPDF, size: number, style: "normal" | "bold" = "normal") {
  doc.setFont("Inter", "normal");
  doc.setFontSize(size);
  // jsPDF variable fonts don't have bold variant — fake it with size
  if (style === "bold") doc.setFontSize(size + 0.5);
}

export async function generatePDF(result: OptimizeResult, lang: Lang) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = 297;
  const pageH = 210;
  const margin = 15;

  await loadFont(doc);
  doc.setFont("Inter", "normal");

  // --- Title page ---
  setFont(doc, 28, "bold");
  doc.text("HolySheet", pageW / 2, 35, { align: "center" });
  setFont(doc, 12);
  doc.setTextColor(100);
  doc.text(t("subtitle", lang), pageW / 2, 45, { align: "center" });
  doc.setTextColor(0);

  // Summary
  const summaryY = 65;
  setFont(doc, 14, "bold");
  doc.text(t("results", lang), margin, summaryY);

  const rows = [
    [t("sheets_used", lang), `${result.total_sheets}`],
    [t("waste", lang), `${result.total_waste_percent.toFixed(1)}%`],
    [t("total_cost", lang), result.total_cost > 0 ? result.total_cost.toFixed(2) : "\u2014"],
  ];

  let y = summaryY + 8;
  for (const [label, value] of rows) {
    setFont(doc, 11, "bold");
    doc.text(label + ":", margin, y);
    setFont(doc, 11);
    doc.text(value, margin + 50, y);
    y += 7;
  }

  // Unplaced
  if (result.unplaced.length > 0) {
    y += 5;
    doc.setTextColor(200, 0, 0);
    setFont(doc, 11, "bold");
    doc.text(t("unplaced_warning", lang) + ":", margin, y);
    setFont(doc, 10);
    y += 6;
    for (const u of result.unplaced) {
      doc.text(`\u2022 ${u.label} (${u.width}\u00d7${u.height}) \u2014 ${u.reason}`, margin + 4, y);
      y += 5;
    }
    doc.setTextColor(0);
  }

  // Cut list
  y += 5;
  setFont(doc, 12, "bold");
  doc.text(t("cut_list", lang), margin, y);
  y += 6;

  const pieceMap = new Map<string, { label: string; w: number; h: number; count: number }>();
  for (const sheet of result.sheets) {
    for (const p of sheet.placements) {
      const key = `${p.label}|${p.width}|${p.height}`;
      const existing = pieceMap.get(key);
      if (existing) existing.count++;
      else pieceMap.set(key, { label: p.label, w: p.width, h: p.height, count: 1 });
    }
  }

  // Table header
  setFont(doc, 9, "bold");
  doc.text(t("label", lang), margin, y);
  doc.text(t("width", lang), margin + 70, y);
  doc.text(t("height", lang), margin + 90, y);
  doc.text(t("quantity", lang), margin + 110, y);
  y += 1;
  doc.setDrawColor(200);
  doc.line(margin, y, margin + 130, y);
  y += 4;

  setFont(doc, 9);
  for (const piece of pieceMap.values()) {
    if (y > pageH - 20) {
      doc.addPage();
      y = margin + 10;
    }
    doc.text(piece.label || "\u2014", margin, y);
    doc.text(`${piece.w}`, margin + 70, y);
    doc.text(`${piece.h}`, margin + 90, y);
    doc.text(`${piece.count}`, margin + 110, y);
    y += 5;
  }

  // Footer on title page
  setFont(doc, 8);
  doc.setTextColor(150);
  doc.text(
    `${t("generated", lang)}: ${new Date().toLocaleDateString(lang === "pl" ? "pl-PL" : "en-US")}`,
    margin,
    pageH - 10,
  );
  doc.text("holysheet.tech", pageW - margin, pageH - 10, { align: "right" });
  doc.setTextColor(0);

  // --- Sheet pages ---
  for (let si = 0; si < result.sheets.length; si++) {
    const sheet = result.sheets[si]!;
    doc.addPage();
    doc.setFont("Inter", "normal");

    setFont(doc, 14, "bold");
    doc.text(
      `${t("sheet_nr", lang)} ${si + 1}${sheet.label ? ` \u2014 ${sheet.label}` : ""}`,
      margin,
      margin,
    );
    setFont(doc, 10);
    doc.text(
      `${sheet.width}\u00d7${sheet.height} mm  |  ${t("waste", lang)}: ${sheet.waste_percent.toFixed(1)}%`,
      margin,
      margin + 6,
    );

    drawSheet(doc, sheet, margin, margin + 12, pageW - margin * 2, pageH - margin * 2 - 25, lang);

    setFont(doc, 8);
    doc.setTextColor(150);
    doc.text(`${si + 1} / ${result.sheets.length}`, pageW - margin, pageH - 8, { align: "right" });
    doc.text("holysheet.tech", margin, pageH - 8);
    doc.setTextColor(0);
  }

  doc.save(`holysheet-${new Date().toISOString().slice(0, 10)}.pdf`);
}

function drawSheet(
  doc: jsPDF,
  sheet: UsedSheet,
  ox: number,
  oy: number,
  maxW: number,
  maxH: number,
  lang: Lang,
) {
  const scale = Math.min(maxW / sheet.width, maxH / sheet.height);
  const cw = sheet.width * scale;
  const ch = sheet.height * scale;
  const startX = ox + (maxW - cw) / 2;
  const startY = oy;

  // Background
  doc.setFillColor(241, 245, 249);
  doc.rect(startX, startY, cw, ch, "F");

  // Grid
  doc.setDrawColor(220);
  doc.setLineWidth(0.1);
  const gridStep = 100;
  for (let x = gridStep; x < sheet.width; x += gridStep) {
    doc.line(startX + x * scale, startY, startX + x * scale, startY + ch);
  }
  for (let y = gridStep; y < sheet.height; y += gridStep) {
    doc.line(startX, startY + y * scale, startX + cw, startY + y * scale);
  }

  // Pieces
  for (const p of sheet.placements) {
    const x = startX + p.x * scale;
    const y = startY + p.y * scale;
    const w = p.width * scale;
    const h = p.height * scale;
    const color = COLORS[p.piece_index % COLORS.length]!;
    const [r, g, b] = hexToRGB(color);

    doc.setFillColor(
      Math.min(255, r + 60),
      Math.min(255, g + 60),
      Math.min(255, b + 60),
    );
    doc.rect(x, y, w, h, "F");

    doc.setDrawColor(r, g, b);
    doc.setLineWidth(0.4);
    doc.rect(x, y, w, h, "S");

    const fontSize = Math.min(14, Math.max(6, Math.min(w, h) * 0.22));
    const cx = x + w / 2;
    const cy = y + h / 2;

    if (w > 8 && h > 6) {
      setFont(doc, fontSize, "bold");
      doc.setTextColor(40);
      doc.text(p.label || "?", cx, cy - fontSize * 0.2, { align: "center", maxWidth: w - 2 });

      setFont(doc, fontSize * 0.8);
      doc.setTextColor(80);
      doc.text(`${p.width}\u00d7${p.height}`, cx, cy + fontSize * 0.4, { align: "center" });

      if (p.rotated) {
        setFont(doc, fontSize * 0.65);
        doc.setTextColor(120);
        doc.text(`\u21bb ${t("rotated", lang)}`, cx, cy + fontSize * 0.9, { align: "center" });
      }
    }
  }

  // Remnants
  for (const r of sheet.remnants) {
    const rx = startX + r.x * scale;
    const ry = startY + r.y * scale;
    const rw = r.width * scale;
    const rh = r.height * scale;

    doc.setFillColor(230, 250, 235);
    doc.rect(rx, ry, rw, rh, "F");

    doc.setDrawColor(34, 197, 94);
    doc.setLineWidth(0.3);
    doc.setLineDashPattern([1.5, 1], 0);
    doc.rect(rx, ry, rw, rh, "S");
    doc.setLineDashPattern([], 0);

    const fontSize = Math.min(11, Math.max(5, Math.min(rw, rh) * 0.18));
    if (rw > 10 && rh > 6) {
      setFont(doc, fontSize);
      doc.setTextColor(34, 140, 70);
      doc.text(
        `${Math.round(r.width)}\u00d7${Math.round(r.height)}`,
        rx + rw / 2,
        ry + rh / 2 + fontSize * 0.15,
        { align: "center" },
      );
    }
  }

  // Sheet border
  doc.setDrawColor(30, 41, 59);
  doc.setLineWidth(0.6);
  doc.rect(startX, startY, cw, ch, "S");

  // Dimension labels
  doc.setTextColor(60);
  setFont(doc, 10);
  doc.text(`${sheet.width} mm`, startX + cw / 2, startY + ch + 5, { align: "center" });
  doc.text(`${sheet.height} mm`, startX - 4, startY + ch / 2, { align: "center", angle: 90 });

  doc.setTextColor(0);
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);
}
